import { useContext, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import betslip from '@gin-betting/betslip';
import core, { GQL } from '@gin-betting/core';
import restrictions from '@gin-betting/restrictions';
import { last, max, min, noop } from 'lodash';
import { createSelector, createStructuredSelector } from 'reselect';

import { Modules, useAsyncModule } from 'async-modules';
import { useDidUpdate, useLastBetSum, usePollingMinStake } from 'hooks';
import type { State } from 'types';
import { DEFAULT_VARIANT_POSITION } from '../constants';
import { RestrictionsContext } from '../restrictions';
import { makeSelectIsFreebet, selectFirstBetslipOdd } from '../selectors';
import useChangeStake from './useChangeStake';
import useSuggests from './useSuggests';

const {
    selectors: { selectLowerMaxBetBetslipBet },
} = restrictions;

const {
    utils: { getBettingOptions },
} = core;

const {
    selectors: { selectBetslipBet },
} = betslip;

interface Input {
    betslipBetId: string;
}

export default function useSetDefaultStake({ betslipBetId }: Input): void {
    const { state: restrictionsState } = useContext(RestrictionsContext);

    const { isLoaded, lastBetSum } = useLastBetSum();
    const { minStake: minBet } = usePollingMinStake();
    const freebetModule = useAsyncModule('freebet');

    const selectProps = useMemo(() => makeSelectProps(freebetModule), [
        freebetModule,
    ]);

    const {
        maxBet,
        stake,
        betslipBet,
        isFreebet,
    } = useSelector((state: State) =>
        selectProps(state, { betslipBetId, restrictionsState })
    );
    const { suggests, isDefaultSuggests } = useSuggests({
        maxStake: `${maxBet}`,
    });

    const changeStake = useChangeStake({ betslipBetId });

    const isStakeGreaterThanMaxBet = !!maxBet && +stake > +maxBet;

    const hasLastBet = !isLoaded || !!lastBetSum;
    const firstBetslipOddId = betslipBet?.betslipOddIds.first();

    const setDefaultStakeRef = useRef(noop);
    setDefaultStakeRef.current = () => {
        if (!firstBetslipOddId || isFreebet) return;

        if (isStakeGreaterThanMaxBet) {
            changeStake(maxBet);

            return;
        }

        if (stake) return;

        if (lastBetSum) {
            const lastOrMax = maxBet ? min([lastBetSum, +maxBet]) : lastBetSum;

            changeStake(`${max([lastOrMax, minBet])}`);

            return;
        }

        const { isAuthorized } = getBettingOptions();

        if (isAuthorized && isDefaultSuggests) return;

        const defaultSuggest =
            suggests[DEFAULT_VARIANT_POSITION] || last(suggests);

        if (defaultSuggest === undefined) return;

        changeStake(`${defaultSuggest}`);
    };

    useEffect(() => {
        if (!firstBetslipOddId) return;

        setDefaultStakeRef.current();
    }, [firstBetslipOddId]);

    useDidUpdate(() => {
        if (!hasLastBet || !isLoaded) return;

        setDefaultStakeRef.current();
    }, [isLoaded, hasLastBet]);

    useDidUpdate(() => {
        if (hasLastBet) return;

        setDefaultStakeRef.current();
    }, [isDefaultSuggests, hasLastBet]);

    useDidUpdate(() => {
        if (!isStakeGreaterThanMaxBet) return;

        setDefaultStakeRef.current();
    }, [isStakeGreaterThanMaxBet]);
}

const selectStake = createSelector(
    selectBetslipBet,
    selectFirstBetslipOdd,
    (betslipBet, betslipOdd) => {
        if (!betslipBet) return '';

        const { stake, mode } = betslipBet;

        if ([GQL.BetType.EXPRESS, GQL.BetType.SYSTEM].includes(mode)) {
            return stake;
        }

        return betslipOdd?.singleStake || '';
    }
);

const makeSelectProps = (freebetModule: Modules['freebet']['default'] | null) =>
    createStructuredSelector({
        maxBet: selectLowerMaxBetBetslipBet,
        stake: selectStake,
        betslipBet: selectBetslipBet,
        isFreebet: makeSelectIsFreebet(freebetModule),
    });
